import React, { useState } from "react";
import useSWRMutation from "swr/mutation";
import useSWR, { mutate } from "swr";
import { z } from "zod";
import { PlusIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { postFetcher } from "api/swr";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import Selector from "components/ui/Selector";
import { Checkbox } from "@/components/ui/checkbox";
import { makeShortId } from "../../../commons";
import { boneParentMap } from "../data/boneTree";

interface FormValues {
  gid: string;
  z: number;
  p: number;
  max: number;
  water: number;
  body: number;
  opt: boolean;
  bone: string;
  category: string;
  name_en: string;
}

const INITIAL_VALUES: FormValues = {
  gid: "",
  z: 0,
  p: 0,
  max: 1,
  water: 0,
  body: 0,
  opt: true,
  bone: "",
  category: "",
  name_en: "",
};

const maxOptions = Array.from({ length: 4 }, (_, i) => ({
  code: i + 1,
  name: String(i + 1),
}));

const zIndexOptions = Array.from({ length: 400 }, (_, i) => ({
  code: i - 199,
  name: String(i - 199),
}));

const playIndexOptions = Array.from({ length: 200 }, (_, i) => ({
  code: i,
  name: String(i),
}));

const boneOptions = Object.keys(boneParentMap).map((bone) => ({
  code: bone,
  name: bone,
}));

// 表单字段组件
function FormField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid w-full max-w-sm items-center gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

const LayerIconCreate: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);

  const { trigger, isMutating } = useSWRMutation("/layericons", postFetcher);
  const { data: LayerGroups = [] } = useSWR(["/layer-groups"]);

  const updateField = <K extends keyof FormValues>(
    key: K,
    value: FormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const selectFile = (fileList: FileList | null) => {
    if (!fileList) return;
    const files = Array.from(fileList);
    setUploadFiles({});

    files.forEach((file) => {
      const img = new Image();
      const name = file.name.split(".")[0];
      img.onload = () => {
        if (img.width !== 256 || img.height !== 256) {
          toast({
            title: "Error",
            variant: "destructive",
            description: "The image size must be 256x256.",
          });
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            setUploadFiles((prev) => ({
              ...prev,
              [name]: reader.result as string,
            }));
          };
          reader.readAsDataURL(file);
        }
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const submitSchema = z.object({
    bone: z.string().min(1, "请选择 bone"),
    files: z.number().min(1, "请上传至少一张图片"),
  });

  const onSubmit = async () => {
    if (isMutating) return;

    const result = submitSchema.safeParse({
      ...values,
      files: Object.keys(uploadFiles).length,
    });
    if (!result.success) {
      toast({
        title: "Validation failed",
        variant: "destructive",
        description: result.error.errors.map((e) => e.message).join("、"),
      });
      return;
    }

    const keys = Object.keys(uploadFiles);
    await Promise.all(
      keys.map((key) =>
        trigger({
          id: makeShortId(3),
          author: "admin",
          name: key,
          code: uploadFiles[key],
          ...values,
        }),
      ),
    );
    // key 是 ['/layericons', params]，需要用 filter 匹配
    mutate((key) => Array.isArray(key) && key[0] === "/layericons", undefined, {
      revalidate: true,
    });
    setOpen(false);
  };

  const hasFiles = Object.keys(uploadFiles).length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2" />
          Create Layer Icon
        </Button>
      </DialogTrigger>
      <DialogContent className="dialog-content overflow-y-auto max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Create Layer Icon</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <FormField id="icon" label="icon *">
          <Input
            id="icon"
            type="file"
            multiple
            accept=".jpg,.png,.jpeg,.webp,.gif,.bmp"
            onChange={(e) => selectFile(e.target.files)}
          />
        </FormField>

        <FormField id="name_en" label="name_en">
          <Input
            id="name_en"
            type="text"
            value={values.name_en}
            onChange={(e) => updateField("name_en", e.target.value)}
          />
        </FormField>

        <FormField id="category" label="category">
          <Input
            id="category"
            type="text"
            value={values.category}
            onChange={(e) => updateField("category", e.target.value)}
          />
        </FormField>

        <FormField id="bone" label="bone *">
          <Selector
            datas={boneOptions}
            update={(e: string) => updateField("bone", e)}
            value={values.bone}
            labelName="bone"
            placeholder="please select bone"
          />
        </FormField>

        <FormField id="group" label="group">
          <Selector
            datas={(LayerGroups as any[]).map((item: any) => ({
              code: item.id,
              name: item.name,
            }))}
            update={(e: string) => updateField("gid", e)}
            value={values.gid}
            labelName="group"
            placeholder="please select group"
          />
        </FormField>

        <FormField id="zIndex" label="zIndex">
          <Selector
            datas={zIndexOptions}
            update={(e: number) => updateField("z", e)}
            value={values.z}
            labelName="zIndex"
            placeholder="please select zIndex"
          />
        </FormField>

        <FormField id="playIndex" label="playIndex">
          <Selector
            datas={playIndexOptions}
            update={(e: number) => updateField("p", e)}
            value={values.p}
            labelName="playIndex"
            placeholder="please select playIndex"
          />
        </FormField>

        <FormField id="maxChoose" label="maxChoose">
          <Selector
            datas={maxOptions}
            update={(e: number) => updateField("max", e)}
            value={values.max}
            labelName="maxChoose"
            placeholder="please select maxChoose"
          />
        </FormField>

        <FormField id="bodymark" label="bodymark">
          <Checkbox
            id="bodymark"
            checked={values.body === 1}
            onCheckedChange={(e) => updateField("body", e ? 1 : 0)}
          />
        </FormField>

        <FormField id="watermark" label="watermark">
          <Checkbox
            id="watermark"
            checked={values.water === 1}
            onCheckedChange={(e) => updateField("water", e ? 1 : 0)}
          />
        </FormField>

        <FormField id="required" label="required">
          <Checkbox
            id="required"
            checked={!values.opt}
            onCheckedChange={(e) => updateField("opt", !e)}
          />
        </FormField>

        <div className="flex flex-wrap">
          {Object.keys(uploadFiles).map((key) => (
            <img
              key={key}
              alt={key}
              className="aspect-square rounded-md border h-24 w-24 me-2"
              src={uploadFiles[key]}
            />
          ))}
        </div>

        <DialogFooter>
          <Button disabled={isMutating} onClick={onSubmit}>
            Submit{isMutating ? "..." : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LayerIconCreate;
